import type { Position, Steuersatz } from './warenkorb';

/**
 * Gutscheine in der Kasse (Gegenstück zu packages/api/src/gutscheine.ts):
 * - Wertgutschein (Mehrzweck): Verkauf als „Bonverkauf" mit 0 % (kein Umsatz, aber
 *   Bareingang und Umsatzzähler), Einlösung als Zahlungsmittel zum Nominalwert.
 * - Einzweck-Gutschein: Verkauf mit dem Steuersatz der Leistung; Einlösung zeigt die
 *   Leistung und zieht den bereits versteuerten Gutschein mit gleichem Satz ab.
 * Die Prüfung macht der Server; die Kasse baut nur Positionen und Rest.
 */
export type GutscheinArt = 'mehrzweck' | 'einzweck';

export interface GutscheinAusgabe {
  code: string;
  art: GutscheinArt;
  nennwertCent: number;
  steuersatz?: Steuersatz;
  bezeichnung?: string;
  gueltigBis?: string;
}

export interface GutscheinEinloesung {
  code: string;
  art: GutscheinArt;
  betragCent: number;
  /** Einzweck: Steuersatz der Leistung (für die negative Position). */
  steuersatz: Steuersatz | null;
}

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_FORMAT =
  /^GS-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}$/;

/** Zufallscode GS-XXXX-XXXX-XXXX (ohne 0/O, 1/I); der Server prüft Format und Eindeutigkeit. */
export function erzeugeGutscheinCode(
  zufall: (anzahl: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n)),
): string {
  const bytes = zufall(12);
  const zeichen = Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length] ?? 'A');
  return `GS-${zeichen.slice(0, 4).join('')}-${zeichen.slice(4, 8).join('')}-${zeichen.slice(8, 12).join('')}`;
}

export function normalisiereCode(text: string): string {
  const roh = text.trim().toUpperCase().replace(/[\s-]/g, '');
  const kern = roh.startsWith('GS') ? roh.slice(2) : roh;
  if (kern.length !== 12) {
    return text.trim().toUpperCase();
  }
  return `GS-${kern.slice(0, 4)}-${kern.slice(4, 8)}-${kern.slice(8, 12)}`;
}

export function istGutscheinCode(text: string): boolean {
  return CODE_FORMAT.test(text);
}

/** Verkaufsposition wie am Server (Bezeichnung trägt den Code, der Server prüft Betrag und Satz). */
export function verkaufsposition(a: GutscheinAusgabe): Position {
  if (a.art === 'mehrzweck') {
    return {
      bezeichnung: `Wertgutschein ${a.code} (Bonverkauf, kein Umsatz)`,
      menge: 1,
      bruttoCent: a.nennwertCent,
      steuersatz: '0',
    };
  }
  return {
    bezeichnung: `Gutschein ${a.bezeichnung ?? 'Leistung'} ${a.code}`,
    menge: 1,
    bruttoCent: a.nennwertCent,
    steuersatz: a.steuersatz ?? '20',
  };
}

export function einzweckEinloesungsposition(
  code: string,
  betragCent: number,
  steuersatz: Steuersatz,
): Position {
  return {
    bezeichnung: `Einzweck-Gutschein ${code} (bereits versteuert)`,
    menge: 1,
    bruttoCent: -betragCent,
    steuersatz,
  };
}

/** Positionen, die zu einem Gutschein gehören (Verkauf oder Einzweck-Einlösung). */
export function istGutscheinPosition(p: Position): boolean {
  return /GS-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}/.test(p.bezeichnung);
}

export function codeAusPosition(p: Position): string | null {
  return /GS-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}/.exec(p.bezeichnung)?.[0] ?? null;
}

/** Mit Mehrzweck-Gutscheinen bezahlter Betrag (Einzweck ist kein Zahlungsmittel). */
export function gutscheinZahlungCent(einloesungen: readonly GutscheinEinloesung[]): number {
  return einloesungen.filter((e) => e.art === 'mehrzweck').reduce((s, e) => s + e.betragCent, 0);
}

/** Offener Rest nach Gutscheinen (bar/Karte zu zahlen). */
export function restNachGutschein(
  summeCent: number,
  einloesungen: readonly GutscheinEinloesung[],
): number {
  return summeCent - gutscheinZahlungCent(einloesungen);
}

/** Ausgaben/Einlösungen behalten, deren Position noch im Bon liegt (Mehrzweck-Einlösungen immer). */
export function bereinigeGutscheine(
  positionen: readonly Position[],
  ausgaben: readonly GutscheinAusgabe[],
  einloesungen: readonly GutscheinEinloesung[],
): { ausgaben: GutscheinAusgabe[]; einloesungen: GutscheinEinloesung[] } {
  const codes = new Set(positionen.map(codeAusPosition).filter((c): c is string => c !== null));
  return {
    ausgaben: ausgaben.filter((a) => codes.has(a.code)),
    einloesungen: einloesungen.filter((e) => e.art === 'mehrzweck' || codes.has(e.code)),
  };
}
